/**
 * DiarizedTranscript.js — Renders speaker-labeled segments emitted by
 * the dictation pipeline (`GET /api/dictation/sessions/:id` →
 * `segments: [{ speaker, text, start, end, cue, pause_before }]`).
 *
 * Pure presentational — no API calls, no state. If `segments` is empty
 * the caller should fall back to the legacy single-line transcript.
 */
import React from 'react';
import { cn } from '../../../lib/utils';
import { Users, Mic } from 'lucide-react';

// Deterministic colour mapping so Speaker A stays Speaker A even when
// the list re-renders. Canonical roles get specific palettes; ordinal
// speakers cycle through a 6-colour wheel.
const ROLE_COLORS = {
  Clinician: { text: 'text-violet-700', dot: 'bg-violet-500', bg: 'bg-violet-50' },
  Patient:   { text: 'text-blue-700',   dot: 'bg-blue-500',   bg: 'bg-blue-50' },
  Nurse:     { text: 'text-emerald-700', dot: 'bg-emerald-500', bg: 'bg-emerald-50' },
};
const SPEAKER_PALETTE = [
  { text: 'text-violet-700',  dot: 'bg-violet-500',  bg: 'bg-violet-50' },
  { text: 'text-blue-700',    dot: 'bg-blue-500',    bg: 'bg-blue-50' },
  { text: 'text-emerald-700', dot: 'bg-emerald-500', bg: 'bg-emerald-50' },
  { text: 'text-amber-700',   dot: 'bg-amber-500',   bg: 'bg-amber-50' },
  { text: 'text-rose-700',    dot: 'bg-rose-500',    bg: 'bg-rose-50' },
  { text: 'text-sky-700',     dot: 'bg-sky-500',     bg: 'bg-sky-50' },
];

export const speakerColor = (speaker, seenOrder = []) => {
  if (!speaker) return SPEAKER_PALETTE[0];
  if (ROLE_COLORS[speaker]) return ROLE_COLORS[speaker];
  const idx = seenOrder.indexOf(speaker);
  const pos = idx === -1 ? seenOrder.length : idx;
  return SPEAKER_PALETTE[pos % SPEAKER_PALETTE.length];
};

export const formatSegmentTime = (seconds) => {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
};

export const SegmentList = ({ segments = [], speakers = null }) => {
  if (!segments || segments.length === 0) return null;
  // Build the speaker-seen order once so colour assignment is stable
  // across rows (avoids "flicker" when a new row appears).
  const seenOrder = [];
  for (const seg of segments) {
    if (seg.speaker && !seenOrder.includes(seg.speaker)) seenOrder.push(seg.speaker);
  }

  return (
    <div data-testid="diarized-segment-list" className="space-y-3">
      {speakers && speakers.count > 0 && (
        <div
          data-testid="diarization-summary"
          className="flex items-center gap-2 text-[10px] text-slate-500 border-b border-slate-100 pb-2"
        >
          <Users className="w-3 h-3" />
          <span className="font-medium">
            {speakers.count} speaker{speakers.count === 1 ? '' : 's'}
          </span>
          {speakers.method && (
            <span className="text-slate-400">· {speakers.method}</span>
          )}
          {typeof speakers.confidence === 'number' && (
            <span
              className="ml-auto text-slate-400"
              data-testid="diarization-confidence"
            >
              confidence {Math.round(speakers.confidence * 100)}%
            </span>
          )}
        </div>
      )}

      {segments.map((seg) => {
        const color = speakerColor(seg.speaker, seenOrder);
        return (
          <div
            key={seg.index}
            className="flex gap-2 text-xs"
            data-testid={`segment-row-${seg.index}`}
            data-speaker={seg.speaker}
          >
            <span
              className="text-slate-400 font-mono w-10 shrink-0 text-[10px] leading-relaxed"
              data-testid={`segment-time-${seg.index}`}
            >
              {formatSegmentTime(seg.start)}
            </span>
            <div className="flex-1">
              <div className="flex items-center gap-1.5">
                <span className={cn('w-1.5 h-1.5 rounded-full', color.dot)} />
                <span
                  className={cn('font-semibold text-[11px]', color.text)}
                  data-testid={`segment-speaker-${seg.index}`}
                >
                  {seg.speaker || 'Speaker A'}
                </span>
                {seg.cue && (
                  <span
                    className="text-[9px] text-slate-400 uppercase tracking-wide"
                    title="Detected from explicit speaker cue"
                  >
                    cue
                  </span>
                )}
              </div>
              <p
                className={cn('text-slate-700 mt-0.5 px-2 py-1 rounded', color.bg)}
                data-testid={`segment-text-${seg.index}`}
              >
                {seg.text}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const EmptySegmentPlaceholder = ({ isRecording = false }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="diarized-empty">
    <div className={cn('h-10 w-10 rounded-full flex items-center justify-center mb-2',
      isRecording ? 'bg-red-50' : 'bg-slate-100')}>
      <Mic className={cn('h-4 w-4', isRecording ? 'text-red-400 animate-pulse' : 'text-slate-300')} />
    </div>
    <p className="text-xs text-slate-500 font-medium">Listening for speaker turns…</p>
  </div>
);
